import { DecisionContext, defaultFinancialYear } from "../common/commonDomain";
import { makeAutoObservable } from "mobx";
import { CompanyRole } from "../../types/CompanyInfo";
import { getFullName, sortedBoard } from "../../utils/Roles";
import { isBefore } from "../../utils/Dates";

import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { BooleanFieldModel, LocalDateFieldModel, NumberFieldModel } from "../../common/commonForms";

export type ApproveCompanyBoardRemuneration = {
  type: DecisionType.approveCompanyBoardRemuneration;
  isReceive?: boolean;
  isDeviatingPeriod?: boolean;
  isCurrentYear?: boolean;
  period?: string;
  deviatingPeriodFrom?: string;
  deviatingPeriodTo?: string;
  remuneration?: Array<Remuneration>;
};

export type Remuneration = {
  id: string;
  name: string;
  value: number;
};

export type RemunerationViewModel = {
  id: string;
  name: string;
  value: NumberFieldModel;
  isSelected: boolean;
};

export function mapRemunerationToInputModel(remuneration: Remuneration): NumberFieldModel {
  return new NumberFieldModel({
    value: remuneration.value,
    required: true,
    isPositiveOrZero: true,
  });
}

export class ApproveCompanyBoardRemunerationForm {
  readonly type = DecisionType.approveCompanyBoardRemuneration;
  isReceive: BooleanFieldModel; // will board members receive any payment?
  isDeviatingPeriod: BooleanFieldModel;
  isCurrentYear: BooleanFieldModel;
  remunerationByRoleId: Map<string, NumberFieldModel>;
  deviatingPeriodFrom: LocalDateFieldModel;
  deviatingPeriodTo: LocalDateFieldModel;
  boardNotSelectedWarning?: TMessage;
  deviatingPeriodSuitableWarning?: TMessage;
  period: string;

  constructor(
    private context: DecisionContext,
    public data?: ApproveCompanyBoardRemuneration,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.isReceive = new BooleanFieldModel({
      value: data?.isReceive,
      required: true,
      onChange: (val) => this.isCurrentYear.setRequired(val),
    });
    this.isDeviatingPeriod = new BooleanFieldModel({
      value: data?.isDeviatingPeriod,
      required: true,
    });
    this.isCurrentYear = new BooleanFieldModel({
      value: data?.isCurrentYear,
      required: data?.isReceive,
      onChange: (val) => {
        this.deviatingPeriodFrom.setRequired(!val);
        this.deviatingPeriodTo.setRequired(!val);
      },
    });
    this.period = data?.period ?? defaultFinancialYear();
    this.remunerationByRoleId = new Map(
      (data?.remuneration ?? []).map((r) => [r.id, mapRemunerationToInputModel(r)]),
    );
    this.deviatingPeriodFrom = new LocalDateFieldModel({
      value: data?.deviatingPeriodFrom,
      required: data?.isReceive && data?.isCurrentYear === false,
    });
    this.deviatingPeriodTo = new LocalDateFieldModel({
      value: data?.deviatingPeriodTo,
      required: data?.isReceive && data?.isCurrentYear === false,
    });
  }

  get boardMembers(): CompanyRole[] {
    return sortedBoard(this.context.companyInfo.roles);
  }

  get remunerations(): RemunerationViewModel[] {
    return this.boardMembers.map((m) => ({
      id: String(m.companyroleId),
      name: getFullName(m),
      value:
        this.remunerationByRoleId.get(String(m.companyroleId)) ??
        new NumberFieldModel({ isPositiveOrZero: true }),
      isSelected: this.remunerationByRoleId.has(String(m.companyroleId)),
    }));
  }

  get selectedRemunerations(): RemunerationViewModel[] {
    return this.remunerations.filter((d) => d.isSelected);
  }

  toggleRemuneration(r: RemunerationViewModel) {
    if (r.isSelected) {
      this.remunerationByRoleId.delete(r.id);
    } else {
      this.remunerationByRoleId.set(
        r.id,
        new NumberFieldModel({
          required: true,
          isPositiveOrZero: true,
        }),
      );
    }
  }

  get showRemunerations() {
    return this.isReceive.value === true;
  }

  get showPeriod() {
    return this.showRemunerations && this.isCurrentYear.value === false;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isRemunerationValid(): boolean {
    if (this.showRemunerations) {
      return (
        this.selectedRemunerations.length > 0 &&
        this.selectedRemunerations.every((r) => r.value.isValid)
      );
    }
    return true;
  }

  validateRemunerations() {
    this.boardNotSelectedWarning = undefined;
    if (this.showRemunerations) {
      this.selectedRemunerations.forEach((d) => d.value.validate());
      if (this.selectedRemunerations.length === 0) {
        this.boardNotSelectedWarning = {
          key: "decisions.approveCompanyBoardRemuneration.receive-remuneration.warning",
        };
      }
    }
  }

  validateDeviatingPeriod(): boolean {
    this.deviatingPeriodSuitableWarning = undefined;
    if (this.showPeriod) {
      this.deviatingPeriodFrom.validate();
      this.deviatingPeriodTo.validate();
      if (
        this.deviatingPeriodFrom.isValid &&
        this.deviatingPeriodTo.isValid &&
        !isBefore(this.deviatingPeriodFrom.inputValue, this.deviatingPeriodTo.inputValue)
      ) {
        this.deviatingPeriodSuitableWarning = {
          key: "decisions.approveCompanyBoardRemuneration.enter-period.warning",
        };
      }
    }
    return true;
  }

  get isValidDeviatingPeriod() {
    if (this.showPeriod) {
      return (
        !this.deviatingPeriodSuitableWarning &&
        this.deviatingPeriodFrom.isValid &&
        this.deviatingPeriodTo.isValid
      );
    }
    return true;
  }

  get isValid() {
    return (
      this.isReceive.isValid &&
      this.isCurrentYear.isValid &&
      this.isRemunerationValid &&
      this.isValidDeviatingPeriod
    );
  }

  validate(): boolean {
    this.isReceive.validate();
    this.isCurrentYear.validate();
    this.validateRemunerations();
    this.validateDeviatingPeriod();
    return this.isValid;
  }
}
